import '../styles/Home.css';

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import Container from '../components/Layout/Container';

import Select from '../components/Select/Select';

import { Calendar, DateObject } from 'react-multi-date-picker';
import DatePickerLocales_ko from '../components/DatePicker/DatePickerLocales_ko';
import '../components/DatePicker/datePickerCustom.css';

import Modal, { useToggleModal } from '../components/Modal/Modal';
import { fetchGet } from '../hooks/fetch';
import { ApplyStatus, ProgramType } from './program/ProgramEnums';
import { select } from 'framer-motion/client';

function SectionTitle(props) {
	const { title, link, children } = props;
	return (
		<div className="sec-top">
			<h2>{title}</h2>
			{link && (
				<Link to={link} className="gs-more-btn" title={`${title} 페이지로 이동`}>
					<span className="sr-only">{`${title} 페이지로 이동`}</span>
				</Link>
			)}
			<div>{children}</div>
		</div>
	);
}

export default function Home() {
	const currentYear = new Date().getFullYear();
	const years = Array.from(
		{ length: currentYear - 2022 + 1 },
		(_, i) => String(currentYear - i)
	);

	const [selected, setSelected] = useState(currentYear);
	const [statistics, setStatistics] = useState({});
	const [popups, setPopups] = useState([]);
	const [programs, setPrograms] = useState([]);
	const [platforms, setPlatforms] = useState([]);
	const [schedules, setSchedules] = useState({});
	const [scheduleMd, setScheduleMd] = useState({});
	const [notices, setNotices] = useState([]);
	const [archives, setArchives] = useState([]);

	function getHomeData() {
		fetchGet(
			`/home`,
			(json) => {
				setPopups(json.popups);
				setPrograms(json.programs);
				setPlatforms(json.platforms);
				setSchedules(json.schedules.schedules);
				setNotices(json.notices);
				setArchives(json.archives);
			}
		);
	}

	function toEokUnitTruncateString(value) {
		if (typeof value !== 'number') return null;

		const eok = Math.floor((value / 100_000_000) * 100) / 100;
		return eok.toFixed(2);
	}

	function getStatistics() {
		fetchGet(
			`/home/statistics?year=${selected}`,
			(json) => setStatistics(json)
		)
	}

	function getDailySchedule(targetDate) {
		const date = targetDate || dateValue;
		const [ year, month, day ] = date.split("-");

		fetchGet(
			`/schedule/daily?year=${year}&month=${month}&day=${day}`,
			(json) => setScheduleMd(json.items)
		)
	}

	useEffect(() => {
		getStatistics();
		getHomeData();
	}, []);

	useEffect(() => {
		getStatistics();
	}, [selected]);

	// 캘린더
	const date = new DateObject();
	const [dateValue, setDateValue] = useState(date);

	// 선택한 날짜
	const [selectedDate, setSelectedDate] = useState(null);

	const toggleModal = useToggleModal();

	// 선택한 날짜의 이벤트 모달
	function handleScheduleEventModal(targetDate) {
		setSelectedDate(targetDate);
		getDailySchedule(targetDate);
		toggleModal.open({ name: "day-event-modal" });
	}

	function getSchedules(targetDate) {
		const { year, month, day } = targetDate || dateValue;

		fetchGet(
			`/home/schedules?year=${year}&month=${month}&day=${day}`,
			(json) => {
				setSchedules(json.schedules);
			}
		)
	}

	const [popupQueue, setPopupQueue] = useState([]);
	const getPopupSetting = () => {
		const token = document.cookie.split('; ')
						.find(c => c.startsWith("popupSetting="))?.split('=')[1];
		if (!token) return {};

		try {
			return JSON.parse(decodeURI(token));
		} catch {
			return {};
		}
	};

	function closeMdByDays(days = 0, uid = 0) {
		let uidList = getPopupSetting();
		const expireAt = new Date();
		expireAt.setDate(expireAt.getDate() + days);

		uidList = {
			...uidList,
			[uid]: expireAt.getTime()
		};

		document.cookie = `popupSetting=${encodeURI(JSON.stringify(uidList))}; path=/`;
		toggleModal.close();
	}

	function moveToPopupLink(linkType, url) {
		if(linkType === "OUT") {
			window.open(url, "_blank");
		} else if(linkType === "IN") {
			window.location.href = url;
		} else if(linkType === "NONE") {
			console.log("연결된 링크가 없습니다.");
		} 
	}

	useEffect(() => {
		if (!popups?.length) return;

		const uidList = getPopupSetting();
		const now = Date.now();

		const queue = popups
			.filter(popup => {
				const viewPopup = uidList[popup.uid];
				return viewPopup === undefined || viewPopup <= now;
			}).map(popup => ({ name: `main-notice-modal-${popup.uid}` }));

		setPopupQueue(queue);
	}, [popups]);

	useEffect(() => {
		if (toggleModal.stack.length === 0 && popupQueue.length > 0) {
			toggleModal.open(popupQueue[0]);
			setPopupQueue(q => q.slice(1));
		}
	}, [toggleModal.stack, popupQueue]);

	return (
		<main id="home-page-wrap">
			<h1 className="sr-only">메인 페이지</h1>

			<section className="main-visual-section">
				<div>
					<div className="item-1">
						<h1>
							<p className="heading-1">
								<span>서울대학교병원의</span>
							</p>
							<p className="heading-2">
								<span>우수한 특허 소개 및</span> <span>특허 출원 지원</span>
							</p>
						</h1>
						<div>
							<span>창업 프로그램을 통해</span>
							<span>
								<strong>창</strong>
								<strong>업</strong>과 <strong>투</strong>
								<strong>자</strong>를 연계 증진
							</span>
						</div>
					</div>
					<div className="item-2">
						<strong>서울대학교병원</strong>
						<span>기술사업화지원센터</span>
					</div>
				</div>
			</section>

			<Container>
				<section className="main-section main-section-1">
					<div className="sec-top">
						<h2>
							<strong>{selected}년 서울대학교병원</strong> <span>기술사업화지원센터 성과</span>
						</h2>
						<Select
							id="performance-select"
							label="구분을 선택해주세요."
							defaultValue={selected}
							onChange={(e) => setSelected(e.target.value)}
						>
							{years?.map((opt, i) => <option key={i} value={opt}>{opt}년</option>)}
						</Select>
					</div>
					<div className="con">
						<ul>
							<li className="item-1">
								<p className="title">
									<small>Patent</small>
									<strong>특허 등록 건수</strong>
								</p>
								<div className="detail d-1">
									<div>
										<small className="sort">국내</small>
										<div className="count">
											<strong>{statistics?.patentDomesticCnt}</strong> <span>건</span>
										</div>
									</div>
									<div>
										<small className="sort">해외</small>
										<div className="count">
											<strong>{statistics?.patentOverseaCnt}</strong> <span>건</span>
										</div>
									</div>
								</div>
							</li>
							<li className="item-2">
								<p className="title">
									<small>Technology Transfer</small>
									<strong>기술이전계약 금액 및 건수</strong>
								</p>
								<div className="detail">
									<div>
										<div className="count">
											<strong>{statistics?.techTransferCnt}</strong> <span>건</span>
										</div>
									</div>
									<div>
										<div className="count">
											<strong>{toEokUnitTruncateString(statistics?.techTransferAmount)}</strong> <span>억원</span>
										</div>
									</div>
								</div>
							</li>
							<li className="item-3">
								<p className="title">
									<small>Startup</small>
									<strong>창업기업 수 및 투자규모</strong>
								</p>
								<div className="detail">
									<div>
										<div className="count">
											<strong>{statistics?.startupCnt}</strong> <span>건</span>
										</div>
									</div>
									<div>
										<div className="count">
											<strong>{toEokUnitTruncateString(statistics?.startupAmount)}</strong> <span>억원</span>
										</div>
									</div>
								</div>
							</li>
						</ul>
					</div>
				</section>

				<section className="main-section main-section-2">
					<SectionTitle title="프로그램" link="./program/list" />
					<div className="gs-program-list">
						<ul>
							{programs?.length === 0 ? (
								<li>
									<div className="gs-no-con">등록된 내용이 없습니다.</div>
								</li>
							) : (
								programs?.slice(0, 3).map((post, i) => (
									<li key={i}>
										<Link to={`./program/list/${post.uid}`}>
											<div className="thumb">
												<img src={`/api-tlo/file/image/${post.thumbnail}`} alt="" />
											</div>
											<div className="gs-infos-badge">
												<div className="info-1">
													<span className={`category ${ProgramType[post.category]?.class}`}>
														{ProgramType[post.category]?.str}
													</span>
													<span className={`state ${ApplyStatus[post.status]?.class}`}>
														{ApplyStatus[post.status]?.str}
													</span>
												</div>
												<div className="info-2">
													<h2>{post.title}</h2>
												</div>
												<div className="info-3">
													<div className="gs-unit pipeline">
														<span>신청기간 : {post.applicationPeriod}</span>
														<span>
															모집인원 : {post.recruitmentCount === 0 ? '제한없음' : `${post.recruitmentCount} 명`}
														</span>
														<span>교육기간 : {post.educationPeriod}</span>
													</div>
												</div>
											</div>
										</Link>
									</li>
								))
							)}
						</ul>
					</div>
				</section>

				<section className="main-section main-section-3">
					<SectionTitle title="기술소개 투자유치 플랫폼" link="./platform/startup" />
					<div className="ms-technology-introduction">
						{platforms?.length === 0 ? (
							<div className="gs-no-con">등록된 내용이 없습니다.</div>
						) : (
							<ul>
								{platforms?.map(
									(post, i) =>
										i < 4 && (
											<li key={i}>
												<Link to={`./platform/startup/${post.uid}`}>
													<dl>
														<dt>{post.company}</dt>
														<dd>{post.summary}</dd>
														<dd className="gs-bullet asterisk">
															대표 : {post.representative} / {post.company}
														</dd>
														<dd className="gs-bullet asterisk">
															질환 : {post.diseases}
														</dd>
														<dd className="gs-bullet asterisk">
															희망 : {post.hope}
														</dd>
													</dl>
												</Link>
											</li>
										),
								)}
							</ul>
						)}
					</div>
				</section>

				<section className="main-section main-section-4">
					<div className="sec-top">
						<SectionTitle title="지원실 안내" />
					</div>
					<div className="ms-support-office-info">
						<div className="item-1 bg">
							<div className="title">
								<h1>일정</h1>
								<Link to="./community/schedule" className="gs-more-btn" title="일정 페이지로 이동">
									<span className="sr-only">일정 페이지로 이동</span>
								</Link>
							</div>
							<div className="gs-calendar-con">
								<Calendar
									showOtherDays
									shadow={false}
									locale={DatePickerLocales_ko}
									formatMonth={(month, year) => month}
									formatYear={(year, month) => year + '년'}
									monthYearSeparator=" "
									format="YYYY-MM-DD"
									value={dateValue}
									onMonthChange={(d) => {
										const newDate = new DateObject(d);
										setDateValue(newDate);
										getSchedules(newDate);             
									}}
									// 일별 이벤트 표시
									mapDays={({ date }) => {
										const showdDate = date.format('YYYY-MM-DD');
										const count = schedules?.[showdDate] || 0;

										return {
											children: (
												<>
													<em title={count > 0 ? `일정 ${count}개` : ''}>
														{date.format('D')}
													</em>
													{count > 0 && <i key={date} className="dot" />}
												</>
											),
										};
									}}
									onChange={(date) => handleScheduleEventModal(date.format('YYYY-MM-DD'))}
								>
									<button
										className="rmdp-calendar-today-btn"
										onClick={() => {
											const today = new DateObject(); 
											setDateValue(today);
											handleScheduleEventModal(today.format('YYYY-MM-DD'));
										}}
									>
										오늘
									</button>
								</Calendar>
							</div>
						</div>
						<div className="item-2">
							<div className="bg">
								<div className="title">
									<h1>공지사항</h1>
									<Link
										to="./community/notice"
										className="gs-more-btn"
										title="공지사항 페이지로 이동"
									>
										<span className="sr-only">공지사항 페이지로 이동</span>
									</Link>
								</div>
								{notices?.length === 0 ? (
									<div className="gs-no-con">등록된 내용이 없습니다.</div>
								) : (
									<ul>
										{notices?.slice(0, 5).map((post, i) => (
											<li key={i} className="gs-bullet">
												<Link
													to={`./community/notice/${post.uid}`}
													className="gs-ellipsis"
													title={`[${post.type}] ${post.title}`}
												>
													<div className="gs-underline-item effect">
														<span className="line">
															<em
																className={
																	post.type === '정보공유'
																		? 'sort-1'
																		: post.type === '구인/구직'
																		? 'sort-2'
																		: post.type === '기타'
																		? 'sort-3'
																		: ''
																}
															>
																[{post.type}]
															</em>{' '}
															{post.title}
														</span>
													</div>
												</Link>
											</li>
										))}
									</ul>
								)}
							</div>
							<div className="bg">
								<div className="title">
									<h1>자료실</h1>
									<Link
										to="./community/archive"
										className="gs-more-btn"
										title="자료실 페이지로 이동"
									>
										<span className="sr-only">자료실 페이지로 이동</span>
									</Link>
								</div>
								{archives?.length === 0 ? (
									<div className="gs-no-con">등록된 내용이 없습니다.</div>
								) : (
									<ul>
										{archives?.slice(0, 5).map((post, i) => (
											<li key={i} className="gs-bullet">
												<Link
													to={`./community/archive/${post.uid}`}
													className="gs-ellipsis"
													title={post.title}
												>
													<div className="gs-underline-item effect">
														<span className="line">{post.title}</span>
													</div>
												</Link>
											</li>
										))}
									</ul>
								)}
							</div>
						</div>
					</div>
				</section>
			</Container>

			{/* 선택한 날짜의 일정 팝업 */}
			{toggleModal.modal?.name === 'day-event-modal' && scheduleMd !== null && scheduleMd?.length > 0 && (
				<Modal
					ariaLabelledby="day-event-modal"
					handleOpenModal={toggleModal.close}
					style={{ maxWidth: '480px' }}
				>
					<Modal.Header id="day-event-modal">
						<span>{selectedDate}</span>{' '}
						<small style={{ fontSize: '20px' }}>({scheduleMd?.length}건)</small>
					</Modal.Header>
					<Modal.Body>
						<div className="gs-txt-list">
							<ul>
								{scheduleMd?.map((event, i) => <li key={i} className="gs-bullet">{event.name}</li>)}
							</ul>
						</div>
					</Modal.Body>
				</Modal>
			)}

			{/* 홈페이지 OPEN 모달 */}
			{popups?.map(popup => {
				const modalName = `main-notice-modal-${popup?.uid}`;
				return (
					toggleModal.modal?.name === modalName && (
						<Modal
							ariaLabelledby="main-notice-modal"
							handleOpenModal={toggleModal.close}
							style={{ maxWidth: '500px' }}
						>
							<Modal.Header id={modalName}>
								<span>{popup?.title}</span>
							</Modal.Header>
							<Modal.Body onClick={() => moveToPopupLink(popup?.linkType, popup?.linkUrl)}>
								<div dangerouslySetInnerHTML={{ __html: popup?.content }} />
							</Modal.Body>
							<Modal.Footer className="md-close-btns">
								<div><button type="button" onClick={() => closeMdByDays(1, popup?.uid)} > 하루 동안 보지 않기 </button></div>
								<div><button type="button" onClick={() => closeMdByDays(7, popup?.uid)} > 일주일 동안 보지 않기 </button></div>
							</Modal.Footer>
						</Modal>
					)
				)
			})}
		</main>
	);
}
